import { useMemo, useState } from "react";
import { ArrowLeft, ArrowUpRight, CalendarDays, Clock3, MapPin, Rss, Search, X } from "lucide-react";
import { ProgrammeGallery } from "./components/ProgrammeGallery";
import { SiteFooter, SiteHeader } from "./components/SiteChrome";
import { ReadingCover } from "./components/TraditionArt";
import {
  type Article,
  type MissionEvent,
  articleHref,
  articles,
  eventHref,
  events,
  formatEditorialDate,
  formatEventDateRange,
  isUpcomingEvent,
  normalisePathname,
} from "./content/editorial";

function ArticleCard({ article, showKeywords = false }: { article: Article; showKeywords?: boolean }) {
  const external = Boolean(article.externalUrl);
  return (
    <article className="article-card">
      <ReadingCover title={article.title} category={article.category} />
      <div className="editorial-meta">
        <span>{article.category}</span>
        <time dateTime={article.publishedDate}>{formatEditorialDate(article.publishedDate)}</time>
      </div>
      <h3><a href={articleHref(article)} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>{article.title}</a></h3>
      <p>{article.summary}</p>
      {showKeywords && article.keywords.length > 0 && (
        <ul className="article-keywords" aria-label={`Keywords for ${article.title}`}>
          {article.keywords.map((keyword) => <li key={keyword}>{keyword}</li>)}
        </ul>
      )}
      <a className="editorial-link" href={articleHref(article)} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>
        {external ? "Read on Substack" : "Read article"} <ArrowUpRight size={16} aria-hidden="true" />
      </a>
    </article>
  );
}

function EventCard({ event }: { event: MissionEvent }) {
  const upcoming = isUpcomingEvent(event);
  const [year, month, day] = event.startDate.split("-");
  const monthName = new Intl.DateTimeFormat("en", { month: "short", timeZone: "UTC" }).format(new Date(`${event.startDate}T00:00:00Z`));
  return (
    <article className="event-card">
      <div className="event-date" aria-hidden="true"><span>{monthName}</span><strong>{day}</strong><small>{year}</small></div>
      <div>
        <p className={upcoming ? "event-status upcoming" : "event-status"}>{upcoming ? "Upcoming" : "Past event"}</p>
        <h3><a href={eventHref(event)}>{event.title}</a></h3>
        <p>{event.summary}</p>
        <div className="event-card-facts">
          {event.endDate && event.endDate !== event.startDate && <span><CalendarDays size={15} aria-hidden="true" />{formatEventDateRange(event)}</span>}
          {event.time && <span><Clock3 size={15} aria-hidden="true" />{event.time}</span>}
          <span><MapPin size={15} aria-hidden="true" />{event.venue}</span>
        </div>
        <a className="editorial-link" href={eventHref(event)}>Event details <ArrowUpRight size={16} aria-hidden="true" /></a>
      </div>
    </article>
  );
}

export function PublishingPreview() {
  const highlightedEvents = [...events].sort((a, b) => b.startDate.localeCompare(a.startDate)).slice(0, 1);
  return (
    <section className="section publishing-preview" id="publishing" aria-labelledby="publishing-title">
      <div className="container">
        <div className="section-heading split publishing-heading">
          <div><p className="eyebrow">Study & gathering</p><h2 id="publishing-title">Articles & events</h2></div>
          <p>Make time for reflection. Discover a teaching, explore a question, or join the life of the Mission.</p>
        </div>
        <div className="publishing-grid">
          <div>
            <div className="publishing-column-heading"><h3>Latest articles</h3><a href="/articles/">View all</a></div>
            <div className="article-preview-list">{articles.filter((article) => article.category !== "Śrīvidyā").slice(0, 3).map((article) => <ArticleCard article={article} key={article.slug} />)}</div>
          </div>
          <div>
            <div className="publishing-column-heading"><h3>Mission events</h3><a href="/events/">View all</a></div>
            {highlightedEvents.length ? highlightedEvents.map((event) => <EventCard event={event} key={event.slug} />) : <EditorialEmpty type="events" />}
          </div>
        </div>
      </div>
    </section>
  );
}

function EditorialEmpty({ type }: { type: "articles" | "events" }) {
  return (
    <div className="editorial-empty">
      <CalendarDays aria-hidden="true" />
      <h3>No {type === "events" ? "upcoming events" : "articles"} published yet</h3>
      <p>New {type} will appear here as they are announced.</p>
    </div>
  );
}

function EditorialShell({ currentPath, children }: { currentPath: string; children: React.ReactNode }) {
  return (
    <div className="site-shell editorial-shell" id="top">
      <a className="skip-link" href="#main">Skip to content</a>
      <SiteHeader currentPath={currentPath} />
      {children}
      <SiteFooter />
    </div>
  );
}

function ArticlesArchive() {
  const [query, setQuery] = useState("");
  const normalisedQuery = normaliseSearch(query);
  const filteredArticles = useMemo(() => {
    const terms = normalisedQuery.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return articles;
    return articles.filter((article) => {
      const searchable = normaliseSearch(article.searchText);
      return terms.every((term) => searchable.includes(term));
    });
  }, [normalisedQuery]);
  const topics = [...new Set(articles.map((article) => article.category))];

  return (
    <EditorialShell currentPath="/articles/">
      <main id="main">
        <header className="editorial-hero">
          <div className="container editorial-hero-grid">
            <div><p className="overline">The Mission’s publication</p><h1>Articles</h1></div>
            <p>Teachings and reflections on Vedic practice, Śrīvidyā, Tantra and the Mission’s living tradition.</p>
          </div>
        </header>
        <section className="section editorial-archive" aria-labelledby="all-articles-title">
          <div className="container">
            <search className="article-search" aria-label="Find a teaching">
              <form action="/articles/" method="get" onSubmit={(event) => event.preventDefault()}>
                <label className="visually-hidden" htmlFor="article-search-input">Search articles</label>
                <div className="search-input-wrap">
                  <Search aria-hidden="true" />
                  <input
                    id="article-search-input"
                    name="q"
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search Śrīvidyā, Upāsanā, Veda…"
                    aria-describedby="article-search-help article-search-status"
                    autoComplete="off"
                  />
                  {query && (
                    <button type="button" className="search-clear" onClick={() => setQuery("")} aria-label="Clear article search">
                      <X aria-hidden="true" />
                    </button>
                  )}
                </div>
                <p id="article-search-help">Search by title, topic, keyword or words within an article.</p>
              </form>
              {topics.length > 0 && (
                <div className="topic-filters" aria-label="Article topics">
                  <span>Explore</span>
                  <button type="button" aria-pressed={!normalisedQuery} onClick={() => setQuery("")}>All topics</button>
                  {topics.map((topic) => (
                    <button
                      type="button"
                      aria-pressed={normalisedQuery === normaliseSearch(topic)}
                      onClick={() => setQuery(topic)}
                      key={topic}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              )}
            </search>

            <div className="archive-toolbar">
              <div>
                <h2 id="all-articles-title">{normalisedQuery ? "Search results" : "All articles"}</h2>
                <p id="article-search-status" className="search-status" aria-live="polite" aria-atomic="true">
                  {normalisedQuery
                    ? `${filteredArticles.length} ${filteredArticles.length === 1 ? "article" : "articles"} found for “${query.trim()}”`
                    : `${articles.length} ${articles.length === 1 ? "article" : "articles"}`}
                </p>
              </div>
              <a className="feed-link" href="/feed.xml"><Rss size={16} aria-hidden="true" /> RSS feed</a>
            </div>
            {filteredArticles.length > 0 ? (
              <div className="article-grid">
                {filteredArticles.map((article) => <ArticleCard article={article} showKeywords key={article.slug} />)}
              </div>
            ) : (
              <div className="search-empty" role="status">
                <Search aria-hidden="true" />
                <h3>No articles found</h3>
                <p>Try another topic, title or keyword.</p>
                <button type="button" className="text-link" onClick={() => setQuery("")}>Clear search</button>
              </div>
            )}
          </div>
        </section>
      </main>
    </EditorialShell>
  );
}

function normaliseSearch(value: string) {
  return value.normalize("NFKD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("en").trim();
}

function EventsArchive() {
  const upcoming = events.filter((event) => isUpcomingEvent(event));
  const past = events.filter((event) => !isUpcomingEvent(event)).sort((a, b) => b.startDate.localeCompare(a.startDate));
  return (
    <EditorialShell currentPath="/events/">
      <main id="main">
        <header className="editorial-hero event-hero">
          <div className="container editorial-hero-grid">
            <div><p className="overline">Gatherings & programmes</p><h1>Events</h1></div>
            <p>Programme announcements, observances and gatherings from Sri Devinatha Mission.</p>
          </div>
        </header>
        <section className="section editorial-archive" aria-label="Mission events">
          <div className={`container events-archive-grid${upcoming.length === 0 ? " past-events-only" : ""}`}>
            {upcoming.length > 0 ? <div>
              <div className="archive-toolbar"><h2 id="upcoming-events-title">Upcoming</h2><a className="feed-link" href="/events.ics"><CalendarDays size={16} aria-hidden="true" /> Calendar feed</a></div>
              <div className="event-list">{upcoming.map((event) => <EventCard event={event} key={event.slug} />)}</div>
            </div> : <div className="events-notice"><p><CalendarDays size={17} aria-hidden="true" /> New gatherings will be shared here. Explore our past events below.</p><a className="feed-link" href="/events.ics">Calendar feed <ArrowUpRight size={15} aria-hidden="true" /></a></div>}
            {past.length > 0 && <aside className="past-events" aria-labelledby="past-events-title"><h2 id="past-events-title">Past events</h2>{past.map((event) => <EventCard event={event} key={event.slug} />)}</aside>}
          </div>
        </section>
      </main>
    </EditorialShell>
  );
}

function ArticleDetail({ article }: { article: Article }) {
  return (
    <EditorialShell currentPath={`/articles/${article.slug}/`}>
      <main id="main" className="editorial-detail">
        <article>
          <header className="detail-header container-narrow">
            <a className="back-link" href="/articles/"><ArrowLeft size={17} aria-hidden="true" /> All articles</a>
            <div className="editorial-meta"><span>{article.category}</span><time dateTime={article.publishedDate}>{formatEditorialDate(article.publishedDate)}</time></div>
            <h1>{article.title}</h1><p>{article.summary}</p>
          </header>
          {article.html && <div className="prose container-narrow" dangerouslySetInnerHTML={{ __html: article.html }} />}
        </article>
      </main>
    </EditorialShell>
  );
}

function EventDetail({ event }: { event: MissionEvent }) {
  const upcoming = isUpcomingEvent(event);
  return (
    <EditorialShell currentPath={`/events/${event.slug}/`}>
      <main id="main" className="editorial-detail event-detail">
        <article>
          <header className="detail-header container-narrow">
            <a className="back-link" href="/events/"><ArrowLeft size={17} aria-hidden="true" /> All events</a>
            <p className={upcoming ? "event-status upcoming" : "event-status"}>{upcoming ? "Upcoming" : "Past event"}</p>
            <h1>{event.title}</h1><p>{event.summary}</p>
            <dl className="event-facts">
              <div><dt>Date</dt><dd><time dateTime={event.startDate}>{formatEventDateRange(event)}</time></dd></div>
              {event.time && <div><dt>Time</dt><dd>{event.time}{event.timezone ? ` · ${event.timezone}` : ""}</dd></div>}
              <div><dt>Venue</dt><dd>{event.venue}{event.address && <small>{event.address}</small>}</dd></div>
            </dl>
            <div className="detail-actions">
              {event.registrationUrl && <a className="button primary" href={event.registrationUrl}>Register <ArrowUpRight size={17} aria-hidden="true" /></a>}
              {event.mapUrl && <a className="text-link" href={event.mapUrl}>View map <ArrowUpRight size={17} aria-hidden="true" /></a>}
            </div>
          </header>
          {event.html && <div className="prose container-narrow" dangerouslySetInnerHTML={{ __html: event.html }} />}
          {event.gallery.length > 0 && (
            <section className="event-gallery-section container-narrow" aria-labelledby="event-gallery-title">
              <p className="eyebrow">Event photographs</p>
              <h2 id="event-gallery-title">From the gathering</h2>
              <ProgrammeGallery title={event.title} slides={event.gallery} />
            </section>
          )}
        </article>
      </main>
    </EditorialShell>
  );
}

export function EditorialRoute({ pathname }: { pathname: string }) {
  const path = normalisePathname(pathname);
  if (path === "/articles/") return <ArticlesArchive />;
  if (path === "/events/") return <EventsArchive />;
  const articleMatch = path.match(/^\/articles\/([^/]+)\/$/);
  const article = articleMatch ? articles.find((item) => item.slug === articleMatch[1]) : undefined;
  if (article && !article.externalUrl) return <ArticleDetail article={article} />;
  const eventMatch = path.match(/^\/events\/([^/]+)\/$/);
  const event = eventMatch ? events.find((item) => item.slug === eventMatch[1]) : undefined;
  if (event) return <EventDetail event={event} />;
  return <ArticlesArchive />;
}
